import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Empty, Segmented, Spin } from 'antd';
import dayjs from 'dayjs';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import useOtherLeavesHistory from '@/components/other-leaves-history/useOtherLeavesHistory';
import { ChipToggle, StatFigure } from '@/components/profile/settingCards';
import { DATE_FORMATS } from '@/utils/formUtils';
import LeaveDocumentActions from './LeaveDocumentActions';

const EVENTS_SHOWN = 8;

/** Work-type enum argb ("cc99ff" / "FF000000") to a CSS hex. */
const toHex = (argb, fallback) => {
  if (!argb || typeof argb !== 'string') return fallback;
  return `#${argb.length === 8 ? argb.slice(2) : argb}`;
};

const fmt = (d) => (d ? dayjs(d).format(DATE_FORMATS.DISPLAY) : '—');

const SectionTitle = ({ children }) => (
  <h3 className="mb-2 text-[11px] font-extrabold uppercase tracking-[0.08em] text-zinc-400">
    {children}
  </h3>
);

/**
 * The "Leaves" tile of the employee profile: every leave type except annual
 * leave, as cards — the totals, one card per type, the months it falls in and
 * the periods themselves. Same data as OtherLeavesHistoryView, which keeps its
 * table layout for the leaves overview. Opens on the current year, the year
 * the tile counts.
 */
export default function EmployeeLeavesPanel({ employeeId, onOpenHoliday }) {
  const { t } = useTranslation(['translations']);
  const th = (key, opts) =>
    t(`otherLeavesHistory.${key}`, { ns: 'translations', ...opts });
  const tu = (key, opts) =>
    t(`employeeProfile.leavesUi.${key}`, { ns: 'translations', ...opts });

  const [year, setYear] = useState(String(dayjs().year()));
  const [types, setTypes] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const { data, loading, error, clearError } = useOtherLeavesHistory(
    employeeId,
    { year, types, includeHoliday: true }
  );
  const go = data?.holidaySummary;

  // A year with no leaves is not offered; fall back to all years.
  useEffect(() => {
    if (data && year && !data.years?.includes(year)) setYear(null);
  }, [data, year]);

  const toggleType = (key, on) =>
    setTypes((prev) => (on ? [...prev, key] : prev.filter((k) => k !== key)));

  const stats = data?.durationStats || {};
  const byType = data?.byType || [];
  const events = data?.events || [];
  const shownEvents = showAll ? events : events.slice(0, EVENTS_SHOWN);
  const maxTypeDays = Math.max(1, ...byType.map((x) => x.totalDays));

  return (
    <Spin spinning={loading}>
      <div className="grid gap-4">
        {error && (
          <Alert
            type="error"
            message={error}
            showIcon
            closable
            onClose={clearError}
          />
        )}

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <Segmented
            size="small"
            value={year || ''}
            onChange={(v) => setYear(v || null)}
            options={[
              { value: '', label: th('allYears') },
              ...(data?.years || []).map((y) => ({ value: y, label: y })),
            ]}
          />
          {(data?.types || []).length > 1 && (
            <div className="flex flex-wrap gap-1.5">
              {data.types.map((tp) => (
                <ChipToggle
                  key={tp.key}
                  value={types.includes(tp.key)}
                  onChange={(on) => toggleType(tp.key, on)}
                  icon={
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{ background: toHex(tp.color, '#a1a1aa') }}
                    />
                  }
                  label={tp.label}
                />
              ))}
            </div>
          )}
        </div>

        <section className="grid grid-cols-4 gap-2">
          <StatFigure
            accent
            label={tu('statTotal')}
            hint={tu('workDaysNote')}
            value={stats.totalDays ?? 0}
          />
          <StatFigure
            label={th('statPeriods')}
            value={stats.totalPeriods ?? 0}
          />
          <StatFigure label={tu('statAvg')} value={stats.avgDays ?? 0} />
          <StatFigure label={tu('statMax')} value={stats.maxDays ?? 0} />
        </section>

        {/* Annual leave by the decisions; its balance lives on the GO tile. */}
        {go?.periods > 0 && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2 text-[12.5px] text-zinc-600">
            <span className="flex-1">
              {tu('holidayLine', {
                period: year || th('allYears'),
                days: go.days,
                count: go.periods,
              })}
              {Object.entries(go.byEntitlementYear || {}).map(([y, n]) => (
                <span key={y} className="text-zinc-500">
                  {' · '}
                  {tu('holidayFromYear', { year: y, count: n })}
                </span>
              ))}
            </span>
            {onOpenHoliday && (
              <button
                type="button"
                onClick={onOpenHoliday}
                className="text-xs font-semibold text-accent hover:underline"
              >
                {tu('openHoliday')} →
              </button>
            )}
          </div>
        )}

        {data && !events.length ? (
          <Empty description={th('noData')} />
        ) : (
          <>
            {byType.length > 0 && (
              <section>
                <SectionTitle>{th('byTypeTitle')}</SectionTitle>
                <div className="grid grid-cols-2 gap-2">
                  {byType.map((row, i) => (
                    <div
                      key={row.key}
                      className={
                        'setting-card rounded-xl border border-zinc-200 bg-white p-2.5' +
                        (i === byType.length - 1 && byType.length % 2 === 1
                          ? ' col-span-2'
                          : '')
                      }
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="h-3 w-3 shrink-0 rounded"
                          style={{ background: toHex(row.color, '#a1a1aa') }}
                        />
                        <span className="min-w-0 flex-1 truncate text-[12.5px] font-bold text-zinc-900">
                          {row.label}
                        </span>
                        <span className="text-[12px] text-zinc-500">
                          {tu('periodsDays', {
                            count: row.count,
                            days: row.totalDays,
                          })}
                        </span>
                      </div>
                      <div className="mt-2 h-1.5 rounded-full bg-zinc-100">
                        <div
                          className="h-1.5 rounded-full"
                          style={{
                            width: `${(row.totalDays / maxTypeDays) * 100}%`,
                            background: toHex(row.color, '#a1a1aa'),
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {byType.length > 0 && (
              <section>
                <SectionTitle>{th('seasonalityTitle')}</SectionTitle>
                <div className="rounded-xl border border-zinc-200 bg-white p-2.5">
                  <ResponsiveContainer width="100%" height={170}>
                    <BarChart
                      data={data.byMonth}
                      margin={{ top: 4, right: 8, bottom: 0, left: -24 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="monthLabel" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      {(data.types || []).map((tp) => (
                        <Bar
                          key={tp.key}
                          dataKey={tp.key}
                          name={tp.label}
                          stackId="months"
                          fill={toHex(tp.color, '#a1a1aa')}
                          maxBarSize={36}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </section>
            )}

            {events.length > 0 && (
              <section>
                <SectionTitle>{th('eventsTitle')}</SectionTitle>
                <div className="divide-y divide-zinc-100 rounded-xl border border-zinc-200 bg-white">
                  {shownEvents.map((e) => (
                    <div
                      key={e.id}
                      className="flex items-center gap-3 px-3 py-2 text-[12.5px]"
                    >
                      <span
                        className="w-[42%] truncate rounded-full px-2.5 py-0.5 text-[11.5px] font-semibold"
                        style={{
                          background: toHex(e.color, '#f4f4f5'),
                          color: toHex(e.textColor, '#18181b'),
                        }}
                      >
                        {e.typeLabel}
                      </span>
                      <span className="flex-1 text-zinc-700">
                        {fmt(e.dateFrom)}
                        {e.dateTo && e.dateTo !== e.dateFrom
                          ? ` – ${fmt(e.dateTo)}`
                          : ''}
                      </span>
                      <span className="font-bold text-zinc-900">
                        {tu('workDaysCount', { count: e.days })}
                      </span>
                      {/* Kept even when empty, so the day counts line up. */}
                      <span className="flex w-[70px] justify-end">
                        {e.documentId && (
                          <LeaveDocumentActions documentId={e.documentId} />
                        )}
                      </span>
                    </div>
                  ))}
                </div>
                {events.length > EVENTS_SHOWN && (
                  <button
                    type="button"
                    className="mt-2 text-xs font-semibold text-accent hover:underline"
                    onClick={() => setShowAll((v) => !v)}
                  >
                    {showAll
                      ? tu('showLess')
                      : tu('showAll', { count: events.length })}
                  </button>
                )}
              </section>
            )}
          </>
        )}
      </div>
    </Spin>
  );
}
